import { css, keyframes } from "styled-components";
import { theme } from "../theme/theme";

export const borderBefore = css`
  position: relative;

  &:before {
    content: "";
    width: 100%;
    position: absolute;
    top: 0;
    height: 1px;
    opacity: 0.9;
    color: ${theme.colors.secondary};
    border: 1px solid ${theme.colors.secondary_opacity};
  }
`;

export const borderAfter = css`
  position: relative;

  &:after {
    content: "";
    width: 100%;
    position: absolute;
    bottom: 0;
    height: 1px;
    opacity: 0.9;
    color: ${theme.colors.secondary};
    border: 1px solid ${theme.colors.secondary_opacity};
  }
`;
export const linkBorderOut = css`
  display: inline-block;
  color: #000;
  text-decoration: none;

  &:after {
    content: "";
    display: block;
    width: 100%;
    height: 1px;
    background: ${theme.colors.font};
    transition: width 0.3s;
  }
  &:hover {
    &:after {
      width: 0;
    }
  }
`;

export const linkBorderIn = css`
  transition: all 200ms ease-in;
  position: relative;
  box-shadow: 0 0 1px rgba(0, 0, 0, 0);
  overflow: hidden;

  :before {
    content: "";
    position: absolute;
    z-index: -1;
    left: 0;
    right: 100%;
    bottom: 0;
    background: ${theme.colors.primaryOld};
    height: 1px;
    transition-property: right;
    transition-duration: 0.3s;
    transition-timing-function: ease-out;
  }
  :hover:before,
  :focus:before,
  :active:before {
    right: 0;
  }
`;

export const ButtonAnimation = css<{ $secondary?: boolean }>`
  transform: perspective(1px) translateZ(0);
  box-shadow: 0 0 1px rgba(0, 0, 0, 0);

  &:before {
    content: "";
    position: absolute;
    z-index: -1;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: inherit;
    background: ${(props) => (props.$secondary ? "#fff" : theme.colors.primary)};
    transform: scaleX(0);
    transform-origin: 0 50%;
    transition-property: transform;
    transition-duration: 0.3s;
    transition-timing-function: ease-out;
  }

  &:focus,
  &:enabled,
  &:active {
    border: 1px solid ${theme.colors.primary};
  }
  &:hover,
  &:active {
    color: ${(props) => (props.$secondary ? theme.colors.primary : "#fff")};
  }

  &:hover&:before,
  &:active&:before {
    transform: scaleX(1);
  }
`;

export const SectionBottomLine = css`
  ::after {
    content: "";
    height: 1px;
    width: 100%;
    border: 1px solid ${theme.colors.secondary_opacity};
    margin-top: ${theme.spaces.margin.vertical_big};
  }
`;

export const fadeIn = keyframes`
  0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
`;

export const fadeInAnimation = css`
  animation: ${fadeIn} 1s ease-in-out;
`;

export const FadeOut = keyframes`
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
`;

export const rotate = keyframes`
from {
   opacity: 0;
   transform: rotate(0deg);
 }
 to {
   opacity: 1;
   transform: rotate(-45deg);
 }
`;

export const heightBounce = keyframes`
  0% {
    height: 0%;
  }
  50% {
    height: 100%;
  } 
  100% {
    height: 0%;
  }`;

export const spin = keyframes`
  from {
      transform:rotate(0deg);
  }
  to {
      transform:rotate(360deg);
  }
`;

export const arrowAnimation = keyframes`
  0%   { 
    transform: translate(-10px,0px) rotate(-45deg); 
    opacity: 0;  
  }
  50%  { 
    opacity: 1;  
  }
  100% 
  { 
    transform:translate(10px,0) rotate(-45deg); 
    opacity: 0; 
  }
`;

// Delicate "breathing" pulse — smoothly fades opacity in/out. Colour-agnostic and
// reusable: apply to any element (e.g. carousel arrows, hints) and set `color`/`fill`
// separately. Respects prefers-reduced-motion (falls back to a static mid opacity).
export const pulseFade = keyframes`
  0%,
  100% {
    opacity: 0.3;
  }
  50% {
    opacity: 0.6;
  }
`;

export const subtlePulse = css`
  animation: ${pulseFade} 3s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 0.45;
  }
`;

/* ---------- torn paper edge (mask) ----------
 * "classic": an 800 x 16 tile repeated along the edge, drawn inside the block (eats 16 px of its padding) — the PDP bands.
 * "bold": the designer's edge (krawedz_papieru.svg, cropped to its torn strip), never distorted: drawn at its own size up to
 * 1080 px wide (a phone sees a slice), scaled with the viewport above that; it lives in a transparent border of its own
 * height, so the block grows by it and the padding stays. Usage: ${tornEdge("both")} on a block with a background. */
const CLASSIC_TILE = { width: 800, height: 16 };
const CLASSIC_PATH =
  "M0 16 L0 11.5 L4 11 L8 10.9 L12 13.9 L16 11.1 L20 11 L24 11.4 L28 11.1 L32 11.5 L36 11.5 L40 15 L44 11 L48 10.1 L52 10.6 L56 10.3 L60 9.7 L64 10.8 L68 9.3 L72 8.9 L76 8.8 L80 9.3 L84 8.6 L88 8.8 L92 9.2 L96 9.9 L100 9.4 L104 8.9 L108 8.7 L112 9.8 L116 10.5 L120 10.7 L124 10.7 L128 11.1 L132 10.9 L136 10.2 L140 10.8 L144 11.3 L148 10.9 L152 10.2 L156 10 L160 9.8 L164 9.7 L168 10 L172 9.7 L176 10.1 L180 10.7 L184 10.2 L188 10.1 L192 10.9 L196 10.2 L200 10.1 L204 10.7 L208 10.2 L212 10.6 L216 11.8 L220 11.7 L224 15 L228 12.4 L232 12.7 L236 12.3 L240 12.5 L244 11.8 L248 11.6 L252 13.1 L256 11.1 L260 10.8 L264 11.3 L268 10.8 L272 10.5 L276 10.9 L280 10.2 L284 9.2 L288 8.9 L292 9.2 L296 8 L300 8 L304 10.4 L308 8.3 L312 7.9 L316 7.2 L320 7.4 L324 7.5 L328 6.6 L332 7.4 L336 7.4 L340 7.3 L344 7 L348 8.1 L352 6 L356 7 L360 7.3 L364 7.4 L368 6.2 L372 5.6 L376 5.9 L380 6.2 L384 5.9 L388 4.8 L392 5.4 L396 5.3 L400 4.3 L404 4 L408 4.8 L412 4.2 L416 4.4 L420 3.7 L424 4.5 L428 3.9 L432 4.9 L436 4.5 L440 7.7 L444 5 L448 5.9 L452 6.4 L456 5.8 L460 5.8 L464 6.4 L468 6.5 L472 7.4 L476 7.1 L480 7.7 L484 8 L488 7.5 L492 6.5 L496 9.4 L500 6.1 L504 6.7 L508 6.2 L512 6.2 L516 5.5 L520 5.3 L524 5.8 L528 5.6 L532 6 L536 5.7 L540 5.4 L544 5.1 L548 5.1 L552 5.4 L556 6 L560 5.7 L564 6 L568 5.2 L572 4.8 L576 4.7 L580 5.7 L584 5.2 L588 5.7 L592 6.2 L596 5.3 L600 5.2 L604 5.9 L608 4.8 L612 4.8 L616 4.2 L620 7.2 L624 6.4 L628 4.3 L632 3.4 L636 4 L640 3.2 L644 2.8 L648 3.1 L652 3.4 L656 4.3 L660 3.8 L664 4.4 L668 7 L672 4.7 L676 5.9 L680 6.5 L684 7 L688 7.5 L692 7.2 L696 8.1 L700 7.5 L704 7.6 L708 9.6 L712 8.1 L716 9 L720 9 L724 10.9 L728 8.4 L732 9 L736 9.1 L740 8.7 L744 9 L748 9.7 L752 9.5 L756 10.2 L760 10.3 L764 10.1 L768 11.5 L772 9.3 L776 9.5 L780 9.5 L784 10.7 L788 10.6 L792 10.6 L796 10.6 L800 11.5 L800 16 Z";
const BOLD_BOX = { width: 1080, height: 32.6, top: 36 };
const BOLD_PATH =
  "M0,0h1080v51c-1.6-.2-4.1.9-5.4,1-2.8.2-6.6-.4-9.8,0s-2.6,2-3.6,2-2.2-1.1-4-.9-5.2,2.2-5.9,2.4c-4.1.8-4.4-.1-9.2,1.7-2.6,1-6.8,1.2-8.8-.6l-6.3,3.7c-2.4-2.2-4.1-1.2-7-1.6l.5-1.5c-1.5-.6-1.5.8-1.8.9-1.2.4-2.7-.8-3.5-.8-2.9,0-5.5,2-8.4,2s-6.9-.8-7.4-1.7c-.8-1.4,1.9-1.8.9-3s-2,1.7-3.1,1.8-2.1-1-3.4-.2c-.6-2.7-1.6-1.9-2.8-2.4s-1-.9-2-1.2c-3-.7-7.2-.8-10,.4v-1.6s-.8.4-.8.4c-.4,0-.2-.7-.5-.8-1.5-.3-4.1-.5-5.7-.6s.4,1.9-1,1-1.3-2.1-1.6-2.1c-1.2,0-1.9,1.2-3,1.1s-.3-.8-.4-.9c-1.2-.4-3.8-.5-5.1-.9-2-.7-5.2-2.9-7.1-3.2s-4,.2-5.5-.1-2-.7-4.2-1.1c-6.1-1-11.2.2-17.2.5s-2.6-1.5-3.1.5h-.8s0-1,0-1c-1.9,2.5-5.6-.5-6.9-.4s-6.4.9-6.7,1c-1.7.5-1.9,2.1-5.2,1.2s-2.4-1.5-6.2-.7-5.8,1.2-7,1.6-2.6,2.5-3.8,3.1-3.8,1.1-5.4,2.1-1.6,1.5-1.7,2.2l-.8-1.1c-2.9,0-5.2,2.4-8.1,2.8s-2.2-.3-3.1-.1-2.1,1.6-3.1,2.1c-6.6,2.9-7.2-3.9-10.6-4.6s-4.3-.1-5,0-1.6.8-2.6.9c-2.5.3-4.2-1.6-5.2-1.5s-.2,1-.6,1.1c-.9.2-1.9-.4-2.5-.3s-1.7,1-3,.8-.3-.9-.7-1c-1.1-.4-2.6-.5-3.7-.6l.4-1.2-.8-.8c-5.1,1.4-8.6-1.5-13.6-1.1.3-1.1,3.2-.4,1.3-2.1-2.6-.3-3.9,1.5-5.6,2.2s-2.1.2-3,.7-2,2.8-2.4,2.8l-11.2-.4c-4.5,2.8-7.5,3.3-12.6,3.3s-1.1-.6-1.5-.6c-.6,0-3.8,2.4-5.2,2.4s-3.3-2.2-5.2-2.5c-5.4-.8-11.5.8-17.8-.8s-3.1-.2-3.4-2.3l-6.9,1-.8-1.5-1,1.5c-1.1-1.9-3-.4-4.1-.6s-1.8-2.1-1.9,0h-.7c-.7-1.9-1.7-1.1-2.7-1.7s-.2-.7-.9-1c-1.7-.6-3.7,0-5.5-.4l.5-1c-.5-.4-8.1-.2-9,0s-.3,1.1-.6,1l-3-1.1c-.8,1.4-5.4,3.7-5.8,1.6l-1.6,1c-.3-1.8-2.9-.3-4.3-1.2s-.8-.5-2.1-1.1,0-1.3-1.4-.9c-.4,2-1.9.1-3.5.5s-.7,1.5-1.1,1.6-1.3-1-2-1.2-3.7-1.2-3.9-1.2c-.9-.1-1.5.9-2.3.7s-1.5-2.2-2.2,0c-1.3-1.9-2,.1-3.6.4-1.6,0-2.1-3.5-5.2-3.5-6.8,0-10.3,3.7-15.8,4.9-9.7,2.1-18.4,1.3-28.1,5.1s-1.9,0-2.6.3-.8,1.6-2.1,2.2-4.4,1.2-6.1,1.7-1.3,1-1.8,1.1c-1.5.2-2.8-1.9-3.9-2.1s-2,.7-4.2,0-3.2-2.7-5.2-.8c0-.7.2-1.3-.1-1.9-.5-1-4.5-2.9-5.1-3.6s-.7-2.9-1-3.9-1.4-.1-.5-1.4h-3.6s.5-1.8.5-1.8l-2.5-.8.5-1.5-2.6.5c.8-1.1-.2-1.1-.9-2.1l-2.7,1c-.3-1.9-3.3-.4-2.7-3-1.5-.5-.6,1.3-1.8,1s-2.1-1.9-3.7-1.9-3.8,2.3-5.3,1.4v1.6c-1.5-.7-2.5.3-3.9.4-5.5.4-10.4,2-15.6,2.4s-2.8-.3-4.1-.3-1.9,1.2-3.6,1-6.7-2.1-8.9-2.5c-4.1-.7-4.8,1.9-7.4,2.5s-6.8-.9-9.2,1.6-.5,1.7-.9,1.9c-1.9,1.3-6.9,1-9.3,1.8s-1.3,1.2-1.7,1.3-4.9-.9-5.1-1c-.9-.4-.8-1.4-.9-1.4-.5-.4-1.3.9-2,.8-1.3-.3-1.5-2.5-3-1.7-1.7,3.7-2.2,1-2.5.9-.7-.2-1.7.2-2.4,0s-.2-1.1-1.1-1.1c-1.5,0-2.4,1.6-4.4.6l-.6,2c-4.3-1.1-6.4,3.1-10.8,1.1l.5-1.5c-1.1.6-3.6,0-4.4.2s-1.2,1.5-2.3,1.3l1-2.6c-2.6,2.5-3.5.8-6.1.1s-1,0-2.9-1-3.1-1.9-4.7-1.7-8.9,2.3-9.3,2.6-.7,1.1-1.2,1.5c-2.1,1.4-5,2.9-5.9,5.2-1.5.6-1.4-2.2-1.8-2.1l-3.7,2.1-.5-1c-2.2,1.8-7.9,0-9.3,2.1l-3.4-1.1c-.4-.4,1.6-2.1,1-2.6-1.2-.9-.8,1.3-1.5,1v-1.5s-3.9,2.6-3.9,2.6c-3.2-2.7-3.5.8-6,1.5v-2.6s-3.3,1.6-3.3,1.6c-2-3.1-5.8-2.5-8.6-3.3-6.1-1.9-5.6-3.8-13.7-3.4s-.2-.9-.6-1c-1.8-.4-4.1-.1-5.1,1.5l-1-1.1-6,2.6c1-1.7-2.6-3.8-3.9-3.9s-2.3.7-3.6.7-3.3-.9-4.2-.7-1.3,1.1-2.1,1.2-2.5-1.2-3.8-.9v2.1c-.6,0-1.2.1-1.8,0-1.1-.2-1.3-.6-.8-1.5-3,1-4.2-.3-6.6-.9s-1.6,1.1-2-.7h-1c-1.5,3.3-4.4-.2-6.9-.4s-2.6.5-4.4.3c-3.9-.5-2.3-2.6-7.1-1.2s-5.3,1.4-6.9,1.1-1.3-1.2-2.2-1.3-4.8.4-5.5.5c-1.1.2-2.4,1.2-3.2,1.9l-.3-1h-1c-.3,2.2-3,1.7-4.4,3.1l-.9-1-6.8,3.1-.5-1.1h-1c-1,2-3.2,2.6-5.3,1.7s-.6-.9-.8-1c-1.5-.7-3.3-.8-4.8-2-.4.1-.8.7-1.2.7-6.5.4-12.6-.3-19.1-.9s-.8,1-3.2-.5-2.3-.3-3.6-.2-2.5-1.3-3.1.7c-1.9-.4-2.9-1.8-4.9-.5v-1.8c-1,0-2.5-1-3.4-1.1-2.1,0-1.7.8-4.8,0s-1,1-2-1.2c-1.7.3-1.1,1.9-1.3,2.1s-1.8-.6-3.2,0c-2.6,1.1-3.8.4-6.3,1s-.3,1-.6,1.1c-1,0-5.8-2.5-6.7-2.4s-1.6,1.1-2.7,1.2c-1.7.2-4.2-.8-6.6-.4s-3.6,1.8-4.8,1.9-3.4-1.6-5.1-1.4-1.2.8-1.7.9c-.8.2-4.8.6-5.3.9-.6.4-1,3.4-3,4.3-1.4-.4-2.6.9-3.5,1.1-1.6.2-3.2-1.2-4.9,0v-1.6c-2.6,1-4.7,1.6-7.4.5s-.4-1-.7-1c-1,0-1.7.9-2,.9s-.9-.8-1.6-.8c-1.1,0-2.2.6-3.6,0l-.6-1.5c-.5.5-2,.9-2.2,1.1s.5,1.4-.6,1.5c.2-3.4-4-1.7-6.2-2.1l1-2.6c-2.1-.5-2,2.1-2.9,2.5s-1.9-.5-2.6.6l-.3-1h-1c-.4,2.8-2.4.9-3.9,1s-1.2.8-1.6.8-2.2-1.5-3.4-.8l1.6-1.5c-1.9,0-3.4-1.3-5.1-1.9s-1.8,0-2.1-.2,0-1-.4-1.5-5.1-2.2-5.8-2.3-1.4.8-2.4.9c-1.4,0-1.7-1-2.6-1.4s-6.5.5-8.3.5-1.3-.7-2.1-.8c-3.7,0-5.8,1-8.8,2s0-1-1-1.1-2.9.9-4.4.6l.5-1.5-3.4.9c-1.7-3.2-4.2-4.5-7.5-2.5v-1.6c-1.8,1.9-4.2-2-4.5-2.1-.6,0-4.3.7-5.2,1-3.1.9-12.2,3.8-15.5,3.7s-2.7-.9-4.1-1c-3.9-.3-7.2,1.7-10.5,2.5s-9.2,2.2-10.3,2-.9-1.5-1.5-1.9l-.8,1.5c-.8.1-.3-.9-1.1-1.2-1.6-.6-3.7,1-4.3-2.4-1.6.2-2.8.7-4.5.4-3.3-.5-6-4.4-9.4-1.9s-.9,1.5-1.4,1.7c-.7.4-1.7.2-2.5.6s-1.3,1.1-1.9,1.2-1.2-.7-1.6-.8c-4.2-.5-4.8.1-7.6-3.7l-1,1-.8-1.3V0Z";
const tearImage = (path: string, box: { width: number; height: number; top?: number }, flip: boolean) => {
  const top = box.top ?? 0;
  return `url("data:image/svg+xml;utf8,${encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='${box.width}' height='${box.height}' viewBox='0 ${top} ${box.width} ${box.height}' preserveAspectRatio='none'><path ${
      flip ? `transform='scale(1 -1) translate(0 -${2 * top + box.height})'` : ""
    } d='${path}' fill='black'/></svg>`
  )}")`;
};

export type TornEdgeSide = "top" | "bottom" | "both";
export type TornEdgeSize = "classic" | "bold";

const BOLD_WIDTH = `max(${BOLD_BOX.width}px, 100vw)`;
const BOLD_HEIGHT = `max(${BOLD_BOX.height}px, ${((BOLD_BOX.height / BOLD_BOX.width) * 100).toFixed(4)}vw)`;

export const tornEdge = (side: TornEdgeSide = "both", size: TornEdgeSize = "classic") => {
  const top = side !== "bottom";
  const bottom = side !== "top";
  /* the body overlaps each torn strip by 1px: at a fractional edge height (bold = 32.6px) the two layers meet inside one pixel row,
     both cover it only partly, and the row shows through as a faint light line */
  const body = (h: string) => `left ${top ? `calc(${h} - 1px)` : "0"} / 100% calc(100% - ${top ? `${h} + 1px` : "0px"} - ${bottom ? `${h} + 1px` : "0px"})`;

  if (size === "classic") {
    const h = `${CLASSIC_TILE.height}px`;
    const layers = [
      top ? `${tearImage(CLASSIC_PATH, CLASSIC_TILE, false)} top left / ${CLASSIC_TILE.width}px ${h} repeat-x` : "",
      bottom ? `${tearImage(CLASSIC_PATH, CLASSIC_TILE, true)} bottom left / ${CLASSIC_TILE.width}px ${h} repeat-x` : "",
      `linear-gradient(#000, #000) ${body(h)} no-repeat`,
    ]
      .filter(Boolean)
      .join(", ");
    return css`
      -webkit-mask: ${layers};
      mask: ${layers};
      -webkit-mask-composite: source-over;
      mask-composite: add;
    `;
  }

  const h = BOLD_HEIGHT;
  const layers = [
    top ? `${tearImage(BOLD_PATH, BOLD_BOX, true)} top left / ${BOLD_WIDTH} ${h} no-repeat` : "",
    bottom ? `${tearImage(BOLD_PATH, BOLD_BOX, false)} bottom left / ${BOLD_WIDTH} ${h} no-repeat` : "",
    `linear-gradient(#000, #000) ${body(h)} no-repeat`,
  ]
    .filter(Boolean)
    .join(", ");
  return css`
    border-top: ${top ? h : "0"} solid transparent;
    border-bottom: ${bottom ? h : "0"} solid transparent;
    -webkit-mask: ${layers};
    mask: ${layers};
    -webkit-mask-composite: source-over;
    mask-composite: add;
  `;
};

/** right inset of a running text block that grows with its container: none on a phone, a ~42rem line in a desktop column, never more
 * than half the row (FAQ answer, 30.09 mock: 678 of 1100). Percentages here resolve against the containing block's width */
export const readableMeasure = css`
  padding-right: clamp(0px, 100% - ${theme.layout.measure}, 50%);
`;

/** hidden on screen, still read by screen readers and crawlers (a heading whose text is baked into an image) */
export const visuallyHidden = css`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
`;
